import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { fetchWishes, addWish, removeWish, mergeWishes } from "../api/wishes";

/**
 * Wishes — per account, not per browser. The heart on every product card.
 *
 * Signed in, the server is the source of truth (GET/POST/DELETE /wishes),
 * so the list follows the shopper onto any device and two people signing in
 * on one browser never share it. Each account also keeps its own local copy
 * (hr_wishlist:42) purely so the hearts paint correctly before the request
 * lands, and so a backend that doesn't have the endpoints yet — or a
 * dropped connection — still behaves sanely rather than losing saves.
 *
 * A guest saves to hr_wishlist (unscoped, as before). At sign-in that list
 * is handed to /wishes/merge once and cleared, so it joins the account
 * rather than leaking into whoever signs in next.
 *
 * **The heart is optimistic, and a real failure rolls it back.** The
 * distinction the code turns on is `unavailable` (404/405 — this backend
 * predates the endpoints) versus any other failure:
 *
 * * `unavailable` keeps the optimistic state. The local copy is the whole
 *   feature on that deployment, and reverting would make the heart
 *   un-tappable for everyone until the backend ships.
 * * anything else — 5xx, 409 at the cap, a dropped connection — puts the
 *   heart back where it was and says so quietly. Leaving it filled would
 *   promise a save that did not happen, which the shopper only discovers
 *   on another device.
 */
const WishlistContext = createContext(null);

const GUEST_KEY = "hr_wishlist";
const keyFor = (accountId) => (accountId == null ? GUEST_KEY : `${GUEST_KEY}:${accountId}`);

// Only what a card needs; the server holds the real record.
const toStored = (p) => ({ id: p.id, name: p.name, price: p.price, image_url: p.image_url, category: p.category });
// /wishes returns wish rows ({ product, price_drop, ... }); the hearts only
// need the products inside them.
const productsFrom = (wishes) => (Array.isArray(wishes) ? wishes.map((w) => w?.product ?? w).filter(Boolean) : []);

const readStored = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed.filter((i) => i && i.id != null) : [];
  } catch {
    return [];
  }
};

const writeStored = (key, items) => {
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {}
};

const clearStored = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

export const WishlistProvider = ({ children }) => {
  const auth = useAuth();
  const accountId = auth?.user?.id ?? null;
  const token = auth?.token ?? null;
  const signedIn = !!(token && accountId != null);
  const storageKey = keyFor(signedIn ? accountId : null);

  const [items, setItems] = useState(() => readStored(keyFor(null)));
  // One line of quiet feedback: a failed save, or the guest sign-in nudge.
  // Never an interruption — no dialog, nothing to dismiss.
  const [notice, setNotice] = useState(null);
  // Ignores a response that a newer toggle has already superseded.
  const requestSeq = useRef(0);
  // The guest nudge appears on the first wish of a visit, not on every one.
  const noticedGuest = useRef(false);

  const announce = useCallback((text, tone = "info") => {
    setNotice({ text, tone, at: Date.now() });
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  // Swap to this account's own list, then reconcile with the server: a
  // guest list is merged in once (and cleared), otherwise it's a plain read.
  useEffect(() => {
    const local = readStored(storageKey);
    setItems(local);
    if (!signedIn) return;

    let cancelled = false;
    const guestItems = readStored(GUEST_KEY);
    const seq = ++requestSeq.current;
    const settle = (wishes) => {
      if (cancelled || seq !== requestSeq.current) return;
      const next = productsFrom(wishes).map(toStored);
      setItems(next);
      writeStored(storageKey, next);
      clearStored(GUEST_KEY);
    };

    (guestItems.length ? mergeWishes(token, guestItems.map((i) => i.id)) : fetchWishes(token))
      .then(settle)
      .catch(() => {
        // No endpoint (yet) or no connection: keep this account's own copy,
        // folding in anything the guest had so those saves aren't lost.
        if (cancelled || seq !== requestSeq.current || !guestItems.length) return;
        const merged = [...local];
        for (const item of guestItems) if (!merged.some((i) => i.id === item.id)) merged.push(item);
        setItems(merged);
        writeStored(storageKey, merged);
        clearStored(GUEST_KEY);
      });

    return () => { cancelled = true; };
  }, [signedIn, storageKey, token]);

  const isWishlisted = useCallback((id) => items.some((i) => i.id === id), [items]);

  const toggleWishlist = useCallback(
    (product) => {
      const saved = items.some((i) => i.id === product.id);
      const previous = items;
      const next = saved ? items.filter((i) => i.id !== product.id) : [...items, toStored(product)];
      setItems(next); // optimistic: the heart responds immediately
      writeStored(storageKey, next);

      if (!signedIn) {
        if (!saved && !noticedGuest.current) {
          noticedGuest.current = true;
          announce("Sign in to keep your wishes", "prompt");
        }
        return;
      }

      const seq = ++requestSeq.current;
      const call = saved
        ? removeWish(token, product.id)
        : addWish(token, product.id, { variantId: product.variant_id ?? null });
      call
        .then((wishes) => {
          if (seq !== requestSeq.current) return; // a later toggle already won
          const server = productsFrom(wishes).map(toStored);
          setItems(server);
          writeStored(storageKey, server);
        })
        .catch((error) => {
          if (seq !== requestSeq.current) return;
          // This backend has no /wishes yet: the local copy IS the feature
          // there, so the optimistic state stands rather than snapping back.
          if (error?.unavailable) return;
          setItems(previous);
          writeStored(storageKey, previous);
          announce(saved ? "Couldn't remove that wish" : "Couldn't save that wish", "error");
        });
    },
    [announce, items, signedIn, storageKey, token]
  );

  const value = useMemo(
    () => ({ items, count: items.length, isWishlisted, toggleWishlist, notice, dismissNotice: () => setNotice(null) }),
    [items, isWishlisted, toggleWishlist, notice]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};

export const useWishlist = () => useContext(WishlistContext);
